// Client-side permission gating (rbac-restore D52).
//
// The gateway sends the caller's permission list on GET /auth/session as
// `workspace.permissions` (the server's own ROLE_PERMISSIONS entry for the role).
// Controls gate on that list with `useCan`; nothing here copies the role table.
//
// `useIsRole` exists only for the few routes the gateway guards with a role check
// rather than a permission (e.g. PATCH /workspaces/{id} is owner/admin in
// gateway/api/workspaces.py `update_workspace`; POST …/transfer is owner-only). Each
// caller cites that backend check in a comment. Role comparisons anywhere else in
// src/ fail the scan test in rbac.test.tsx.
import type { Permission, Role, SessionInfo } from '../api/types'
import { useSession } from '../context/SessionContext'

/** Pure check: does this session carry `permission`? Missing list ⇒ false (fail closed). */
export function can(session: SessionInfo | null | undefined, permission: Permission): boolean {
  return session?.workspace.permissions?.includes(permission) ?? false
}

/** Pure check: is the caller's workspace role one of `roles`? */
export function isRole(session: SessionInfo | null | undefined, ...roles: Role[]): boolean {
  const role = session?.workspace.role
  return role !== undefined && roles.includes(role)
}

/**
 * Is a workspace MEMBER ROW the owner? Not a gate on the caller: account basics D39 makes
 * the gateway refuse to remove or re-role the owner (409, gateway/api/workspaces.py
 * `remove_member` / `change_member_role`) and refuse a transfer to them, so the UI offers
 * none of those on that row.
 */
export function isOwnerRole(role: Role | null | undefined): boolean {
  return role === 'owner'
}

/** True when the signed-in caller holds `permission` in the active workspace. */
export function useCan(permission: Permission): boolean {
  const { session } = useSession()
  return can(session, permission)
}

/** True when the caller's role is one of `roles`. Only for server-side role checks; cite them. */
export function useIsRole(...roles: Role[]): boolean {
  const { session } = useSession()
  return isRole(session, ...roles)
}
